import { z } from 'zod';

import { get } from 'lodash';
import { normalizeSpaces } from '../friendly';
import {
  SampleSpecRow,
  normalizeSampleType,
  normalizeSamplingPointSubType,
  parseCoordinatesCell,
  parseDateCell,
  parseNumberCell,
  parseSampleMetadataNumberCell,
  parseTextCell,
  sampleSpecColumnKeys,
  zSampleSpecRow,
  isUnavailableSampleMetadataValue,
} from '../sample-sheet';
import { SampleSubType, SampleType, getParentType, isWaterType } from '../sampling-point-types';
import type { RowValidationContext, RowValidationIssues, SampleSheetDraftRow } from './types';

const zSampleSpecRowWithOptionalId = zSampleSpecRow.extend({
  sampleId: zSampleSpecRow.shape.sampleId.nullable(),
});

type CountryValidationLookups = {
  validCodes: Set<string>;
  nameToCode: Map<string, string>;
};

type FieldIssueMessage = {
  required?: string;
  invalid?: string;
  enum?: string;
  date?: string;
};

const numericFieldMessages = new Map<keyof SampleSpecRow, string>([
  ['flowRate', 'Enter the flow rate in m3/day as a number (e.g., 52000). Use a dot for decimals.'],
  ['operatedFlowRate', 'Enter the operated flow rate in m3/day as a number (e.g., 48000). Use a dot for decimals.'],
  ['suspendedSolids', 'Enter suspended solids in mg/L as a number (e.g., 75.5). Use a dot for decimals.'],
  ['bod', 'Enter the BOD in mg/L as a number (e.g., 250). Use a dot for decimals.'],
  ['filteredVolume', 'Enter the filtered volume in mL as a number (e.g., 500). Use a dot for decimals.'],
  ['primElutedDnaVolume', 'Enter the eluted DNA volume in uL as a number (e.g., 120). Use a dot for decimals.'],
  [
    'primElutedDnaConcentration',
    'Enter the eluted DNA concentration in ng/uL as a number (e.g., 45.2). Use a dot for decimals.',
  ],
  [
    'primElutedDnaQuality280',
    'Enter the 260/280 purity ratio between 1.7 and 2.1 (e.g., 1.92). Use a dot for decimals.',
  ],
  ['primElutedDnaQuality230', 'Enter the 260/230 purity ratio as a number (e.g., 1.95). Use a dot for decimals.'],
  [
    'primDilutedDnaConcentration',
    'Enter the diluted DNA concentration (target 10 ng/uL, e.g., 10). Use a dot for decimals.',
  ],
  [
    'primDilutedDnaQuality280',
    'Enter the diluted DNA 260/280 ratio between 1.7 and 2.1 (e.g., 1.89). Use a dot for decimals.',
  ],
  ['primDilutedDnaQuality230', 'Enter the diluted DNA 260/230 ratio as a number (e.g., 1.93). Use a dot for decimals.'],
  ['primDilutedDnaVolume', 'Enter the diluted DNA volume in uL as a number (e.g., 100). Use a dot for decimals.'],
]);

const requiredNumericFields = new Set<keyof SampleSpecRow>([
  'primElutedDnaVolume',
  'primElutedDnaConcentration',
  'primDilutedDnaVolume',
  'primDilutedDnaConcentration',
]);

export const sampleMetadataNumberFieldKeys = [
  'flowRate',
  'operatedFlowRate',
  'suspendedSolids',
  'bod',
  'filteredVolume',
  'primElutedDnaVolume',
  'primElutedDnaConcentration',
  'primElutedDnaQuality280',
  'primElutedDnaQuality230',
  'primDilutedDnaConcentration',
  'primDilutedDnaQuality280',
  'primDilutedDnaQuality230',
  'primDilutedDnaVolume',
] as const satisfies ReadonlyArray<keyof SampleSpecRow>;

export const confirmableSampleMetadataFieldKeys = [
  'filteredVolume',
  'primElutedDnaVolume',
  'primElutedDnaConcentration',
  'primElutedDnaQuality280',
  'primElutedDnaQuality230',
  'primDilutedDnaConcentration',
  'primDilutedDnaQuality280',
  'primDilutedDnaQuality230',
  'primDilutedDnaVolume',
] as const satisfies ReadonlyArray<keyof SampleSpecRow>;

export type ConfirmableSampleMetadataField = (typeof confirmableSampleMetadataFieldKeys)[number];
export type SampleMetadataLimitationCategory = 'volume_normalisation' | 'sample_volume' | 'sample_quality';
export type SampleMetadataLimitationReason = 'missing' | 'not_available' | 'below_recommended_minimum';
export type RowMetadataLimitation = {
  field: ConfirmableSampleMetadataField;
  category: SampleMetadataLimitationCategory;
  reason: SampleMetadataLimitationReason;
  value: number | null;
  recommendedMinimum?: number;
  absoluteMinimum?: number;
};

const confirmableSampleMetadataFields = new Set<keyof SampleSpecRow>(confirmableSampleMetadataFieldKeys);

const primaryDnaDetailNumericFieldList = [
  'primElutedDnaVolume',
  'primElutedDnaConcentration',
  'primElutedDnaQuality280',
  'primElutedDnaQuality230',
  'primDilutedDnaVolume',
  'primDilutedDnaConcentration',
  'primDilutedDnaQuality280',
  'primDilutedDnaQuality230',
] as const satisfies ReadonlyArray<keyof SampleSpecRow>;

const primaryDnaDetailNumericFields = new Set<keyof SampleSpecRow>(primaryDnaDetailNumericFieldList);

const partialPrimaryDnaDetailsMessage =
  'Either leave all optional DNA detail fields empty for Resistomap to measure, or fill all of them before submitting.';

export const primaryDnaDetailWorkbookMixedMessage =
  'Use one DNA-detail approach for the whole workbook: either leave the optional DNA detail fields empty for all samples for Resistomap to measure, or fill them for all samples.';

const adminBypassableFields = new Set<keyof SampleSheetDraftRow>(['primElutedDnaVolume', 'primElutedDnaConcentration']);

const fieldIssueMessages: Partial<Record<keyof SampleSheetDraftRow, FieldIssueMessage>> = {
  sampleDescription: {
    required: 'Describe the sampling point (e.g., Primary clarifier effluent).',
    invalid: 'Enter a text description for the sampling point (e.g., Primary clarifier effluent).',
  },
  sampleType: {
    required: 'Choose a sample type from the dropdown (e.g., Wastewater).',
    enum: 'Choose a sample type from the dropdown (e.g., Wastewater).',
    invalid: 'Choose a sample type from the dropdown (e.g., Wastewater).',
  },
  detailedSampleType: {
    required: 'Choose a sample subtype that matches the selected sample type (e.g., WWTP_INFLUENT).',
    enum: 'Choose a sample subtype that matches the selected sample type (e.g., WWTP_INFLUENT).',
    invalid: 'Choose a sample subtype that matches the selected sample type (e.g., WWTP_INFLUENT).',
  },
  country: {
    required: 'Select a country using its ISO alpha-3 code (e.g., FIN) or the dropdown.',
    invalid: 'Select a country using its ISO alpha-3 code (e.g., FIN) or the dropdown.',
  },
  stateOrRegion: {
    invalid: 'Enter the state or region name (e.g., Uusimaa) or leave the field blank.',
  },
  city: {
    invalid: 'Enter the city or municipality name (e.g., Helsinki) or leave the field blank.',
  },
  latitudeLongitude: {
    invalid:
      'Enter latitude [-90,90] and longitude [-180,180] in decimal degrees (e.g., 60.170451, 24.939575). You can find the coordinates for a by placing a pin on Google Maps.',
  },
  sampleCollectionDate: {
    required: 'Enter a valid date (e.g., 2024-03-25, Mar 25 2024).',
    date: 'Enter a valid date (e.g., 2024-03-25, Mar 25 2024).',
    invalid: 'Enter a valid date (e.g., 2024-03-25, Mar 25 2024).',
  },
  primDnaExtractionKit: {
    invalid: 'Enter the DNA extraction kit name as text (e.g., DNeasy PowerWater) or leave the field blank.',
  },
  flowRate: {
    invalid: numericFieldMessages.get('flowRate'),
  },
  operatedFlowRate: {
    invalid: numericFieldMessages.get('operatedFlowRate'),
  },
  suspendedSolids: {
    invalid: numericFieldMessages.get('suspendedSolids'),
  },
  bod: {
    invalid: numericFieldMessages.get('bod'),
  },
  filteredVolume: {
    required: 'Enter the filtered volume before submitting the sample.',
    invalid: numericFieldMessages.get('filteredVolume'),
  },
  primElutedDnaVolume: {
    required: 'Enter the eluted DNA volume before submitting the sample.',
    invalid: numericFieldMessages.get('primElutedDnaVolume'),
  },
  primElutedDnaConcentration: {
    required: 'Enter the eluted DNA concentration before submitting the sample.',
    invalid: numericFieldMessages.get('primElutedDnaConcentration'),
  },
  primElutedDnaQuality280: {
    invalid: numericFieldMessages.get('primElutedDnaQuality280'),
  },
  primElutedDnaQuality230: {
    invalid: numericFieldMessages.get('primElutedDnaQuality230'),
  },
  primDilutedDnaConcentration: {
    required: 'Enter the diluted DNA concentration before submitting the sample.',
    invalid: numericFieldMessages.get('primDilutedDnaConcentration'),
  },
  primDilutedDnaQuality280: {
    invalid: numericFieldMessages.get('primDilutedDnaQuality280'),
  },
  primDilutedDnaQuality230: {
    invalid: numericFieldMessages.get('primDilutedDnaQuality230'),
  },
  primDilutedDnaVolume: {
    required: 'Enter the diluted DNA volume before submitting the sample.',
    invalid: numericFieldMessages.get('primDilutedDnaVolume'),
  },
};

function createCountryValidationLookups(
  countryNameByAlpha3: RowValidationContext['countryNameByAlpha3'],
): CountryValidationLookups | null {
  if (!countryNameByAlpha3) {
    return null;
  }
  const entries = Object.entries(countryNameByAlpha3);
  if (entries.length === 0) {
    return null;
  }

  const validCodes = new Set<string>();
  const nameToCode = new Map<string, string>();

  entries.forEach(([rawCode, rawName]) => {
    const code = rawCode.trim().toUpperCase();
    if (code) {
      validCodes.add(code);
    }
    const normalizedName = normalizeSpaces(rawName).trim().toLowerCase();
    if (normalizedName) {
      nameToCode.set(normalizedName, code);
    }
  });

  return {
    validCodes,
    nameToCode,
  } satisfies CountryValidationLookups;
}

export function validateRow(row: SampleSheetDraftRow, context: RowValidationContext = {}): RowValidationIssues {
  if (isRowEmpty(row)) {
    return {};
  }

  const issues: RowValidationIssues = {};
  const candidate: Partial<Record<keyof SampleSpecRow, unknown>> = {};
  const countryLookups = createCountryValidationLookups(context.countryNameByAlpha3);
  // Determine control status early for numeric required checks
  const sampleTypeHint = normalizeSampleType(row.sampleType);
  const isControlRowHint = sampleTypeHint === SampleType.CONTROL;

  sampleSpecColumnKeys.forEach(key => {
    const numericMessage = numericFieldMessages.get(key);
    if (numericMessage) {
      const parsedNumber = parseSampleMetadataNumberCell(row[key]);
      if (parsedNumber === undefined) {
        addIssue(issues, key, numericMessage);
        candidate[key] = null;
        return;
      }
      if (
        parsedNumber === null &&
        requiredNumericFields.has(key) &&
        !isControlRowHint &&
        !canSkipPrimaryDnaRequirement(key, context) &&
        !canConfirmMetadataLimitation(key, context)
      ) {
        addIssue(
          issues,
          key,
          fieldIssueMessages[key]?.required ?? 'Enter a numeric value before submitting this field.',
        );
        candidate[key] = null;
        return;
      }
      candidate[key] = parsedNumber;
      return;
    }

    switch (key) {
      case 'sampleDescription': {
        const description = parseTextCell(row.sampleDescription);
        if (description == null) {
          addIssue(
            issues,
            key,
            description === undefined ? fieldIssueMessages[key]?.invalid : fieldIssueMessages[key]?.required,
          );
        }
        candidate[key] = description ?? null;
        return;
      }
      case 'sampleId': {
        const parsedSampleId = parseNumberCell(row.sampleId);
        if (parsedSampleId === undefined) {
          addIssue(issues, key, 'Enter a valid number.');
          candidate[key] = null;
          return;
        }
        if (parsedSampleId != null) {
          if (!Number.isInteger(parsedSampleId) || parsedSampleId <= 0) {
            addIssue(issues, key, 'Enter a positive whole number.');
            candidate[key] = null;
            return;
          }
        }
        candidate[key] = parsedSampleId;
        return;
      }
      case 'sampleCollectionDate': {
        const parsedDate = parseDateCell(row.sampleCollectionDate);
        if (parsedDate === undefined) {
          addIssue(issues, key, 'Enter a valid date (e.g. 2024-03-25, Mar 25 2024, 25 Mar 2024).');
          candidate[key] = null;
          return;
        }
        candidate[key] = parsedDate;
        return;
      }
      case 'latitudeLongitude': {
        const parsedCoordinates = parseCoordinatesCell(row.latitudeLongitude);
        if (parsedCoordinates === undefined) {
          addIssue(issues, key, fieldIssueMessages.latitudeLongitude?.invalid);
          candidate[key] = null;
          return;
        }
        candidate[key] = parsedCoordinates;
        return;
      }
      case 'sampleType': {
        const parsedType = normalizeSampleType(row.sampleType);
        if (parsedType === undefined) {
          addIssue(issues, key, fieldIssueMessages.sampleType?.invalid ?? 'Choose a sample type from the dropdown.');
          candidate[key] = null;
          return;
        }
        candidate[key] = parsedType;
        return;
      }
      case 'detailedSampleType': {
        const parsedType = normalizeSampleType(row.sampleType);
        const subtype = normalizeSamplingPointSubType(row.detailedSampleType, parsedType ?? null);
        if (subtype === undefined) {
          addIssue(
            issues,
            key,
            fieldIssueMessages.detailedSampleType?.invalid ??
              'Choose a sample subtype that matches the selected sample type.',
          );
          candidate[key] = null;
          return;
        }
        candidate[key] = subtype;
        return;
      }
      case 'country': {
        const parsedCountry = parseTextCell(row.country);
        const normalizedSampleType = normalizeSampleType(row.sampleType);
        const canSkipCountry = normalizedSampleType === SampleType.CONTROL;
        if (parsedCountry === undefined) {
          addIssue(
            issues,
            key,
            fieldIssueMessages.country?.invalid ?? 'Select a country using its ISO alpha-3 code (e.g., FIN).',
          );
          candidate[key] = null;
          return;
        }
        if (parsedCountry === null || parsedCountry.trim() === '') {
          if (canSkipCountry) {
            candidate[key] = null;
            return;
          }
          addIssue(
            issues,
            key,
            fieldIssueMessages.country?.required ?? 'Select a country using its ISO alpha-3 code (e.g., FIN).',
          );
          candidate[key] = null;
          return;
        }

        const normalizedCountry = normalizeSpaces(parsedCountry).trim();
        if (!countryLookups) {
          candidate[key] = normalizedCountry;
          return;
        }

        const upperCandidate = normalizedCountry.toUpperCase();
        if (countryLookups.validCodes.has(upperCandidate)) {
          candidate[key] = upperCandidate;
          return;
        }

        const normalizedNameKey = normalizedCountry.toLowerCase();
        const canonical = countryLookups.nameToCode.get(normalizedNameKey);
        if (canonical) {
          candidate[key] = canonical;
          return;
        }

        addIssue(
          issues,
          key,
          fieldIssueMessages.country?.invalid ?? 'Select a country using its ISO alpha-3 code (e.g., FIN).',
        );
        candidate[key] = null;
        return;
      }
      default: {
        const value = get(row, key, undefined);
        candidate[key] = value === undefined ? null : value;
      }
    }
  });

  const candidateSampleType = candidate.sampleType as SampleType | null | undefined;
  const candidateSubtype = candidate.detailedSampleType as SampleSubType | undefined;
  if (candidateSampleType && candidateSubtype && getParentType(candidateSubtype) !== candidateSampleType) {
    addIssue(issues, 'detailedSampleType', 'Choose a sample subtype that matches the selected sample type.');
  }

  const candidateCollectionDate = candidate.sampleCollectionDate as Date | null | undefined;
  const canSkipCollectionDate = candidateSampleType === SampleType.CONTROL;
  if (!canSkipCollectionDate && !candidateCollectionDate && !issues.sampleCollectionDate) {
    addIssue(issues, 'sampleCollectionDate', 'Enter the sample collection date unless the sample is a control.');
  }

  if (candidateSampleType && isWaterType(candidateSampleType)) {
    const filteredVolume = candidate.filteredVolume as number | null | undefined;
    if (filteredVolume == null && !context.allowConfirmableMetadataLimitations) {
      addIssue(
        issues,
        'filteredVolume',
        fieldIssueMessages.filteredVolume?.required ?? 'Enter the filtered volume before submitting the sample.',
      );
    }
  }

  const candidateForZod: Partial<Record<keyof SampleSpecRow, unknown>> = { ...candidate };
  if (candidateSampleType === SampleType.CONTROL && candidateForZod.country == null) {
    candidateForZod.country = '';
  }
  const parsed = zSampleSpecRowWithOptionalId.safeParse(candidateForZod);
  if (!parsed.success) {
    parsed.error.issues.forEach(issue => {
      const pathKey = issue.path[0];
      if (typeof pathKey !== 'string') {
        return;
      }
      if (!isSampleSpecKey(pathKey) || derivedKeys.has(pathKey)) {
        return;
      }
      const sampleKey = pathKey as keyof SampleSheetDraftRow;
      addIssue(issues, sampleKey, formatZodIssue(issue, sampleKey));
    });
  }

  if (candidateSampleType !== SampleType.CONTROL) {
    enforceAllOrNothingPrimaryDnaDetails(issues, candidate, context);
  }

  // Enforce hard minimum diluted volume per selected layout (or default 100 uL when no template selected)
  if (candidateSampleType !== SampleType.CONTROL) {
    const minDiluted =
      typeof context.dilutedVolumeMin === 'number' && Number.isFinite(context.dilutedVolumeMin)
        ? context.dilutedVolumeMin
        : 100;
    const dilutedVol = candidate.primDilutedDnaVolume as number | null | undefined;
    const absoluteMin =
      typeof context.dilutedVolumeAbsoluteMin === 'number' && Number.isFinite(context.dilutedVolumeAbsoluteMin)
        ? context.dilutedVolumeAbsoluteMin
        : null;
    const isConfirmableLowVolume =
      context.allowConfirmableMetadataLimitations === true &&
      absoluteMin != null &&
      typeof dilutedVol === 'number' &&
      Number.isFinite(dilutedVol) &&
      dilutedVol >= absoluteMin;
    if (
      typeof dilutedVol === 'number' &&
      Number.isFinite(dilutedVol) &&
      dilutedVol < minDiluted &&
      !isConfirmableLowVolume
    ) {
      addIssue(
        issues,
        'primDilutedDnaVolume',
        absoluteMin != null && dilutedVol < absoluteMin
          ? `Enter at least ${absoluteMin} uL; lower volumes cannot be analysed.`
          : `Enter at least ${minDiluted} uL for the selected panel/layout.`,
      );
    }
  }

  // Cross-field requirement for DNA purity ratios is skipped for CONTROL samples
  if (
    candidateSampleType !== SampleType.CONTROL &&
    !context.allowMissingPrimaryDnaDetails &&
    !context.allowConfirmableMetadataLimitations
  ) {
    const missingQualityFields = getMissingRequiredQualityFields(candidate);
    if (missingQualityFields.length > 0) {
      const message = 'Provide both 260/280 and 260/230 purity ratios for either eluted DNA or diluted DNA.';
      missingQualityFields.forEach(key => {
        addIssue(issues, key, message);
      });
    }
  }

  return issues;
}

const derivedKeys = new Set<keyof SampleSpecRow>(['sampleId', 'sampleNumber', 'bioReplica']);

function isSampleSpecKey(value: string): value is keyof SampleSpecRow {
  return (sampleSpecColumnKeys as string[]).includes(value);
}

function canSkipPrimaryDnaRequirement(key: keyof SampleSpecRow, context: RowValidationContext): boolean {
  if (key === 'primElutedDnaVolume' && context.requirePrimaryElutedDnaVolume) {
    return false;
  }
  return context.allowMissingPrimaryDnaDetails === true && primaryDnaDetailNumericFields.has(key);
}

function canConfirmMetadataLimitation(key: keyof SampleSpecRow, context: RowValidationContext): boolean {
  return context.allowConfirmableMetadataLimitations === true && confirmableSampleMetadataFields.has(key);
}

function getOptionalPrimaryDnaDetailFields(context: RowValidationContext): ReadonlyArray<keyof SampleSpecRow> {
  return primaryDnaDetailNumericFieldList.filter(
    key => !(key === 'primElutedDnaVolume' && context.requirePrimaryElutedDnaVolume),
  );
}

function isPresentNumericCandidate(candidate: Partial<Record<keyof SampleSpecRow, unknown>>, key: keyof SampleSpecRow) {
  const value = candidate[key];
  return typeof value === 'number' && Number.isFinite(value);
}

function enforceAllOrNothingPrimaryDnaDetails(
  issues: RowValidationIssues,
  candidate: Partial<Record<keyof SampleSpecRow, unknown>>,
  context: RowValidationContext,
) {
  if (!context.allowMissingPrimaryDnaDetails || context.allowMissingElutedDnaBypass) {
    return;
  }
  const optionalFields = getOptionalPrimaryDnaDetailFields(context);
  const presentFields = optionalFields.filter(key => isPresentNumericCandidate(candidate, key));
  if (presentFields.length === 0 || presentFields.length === optionalFields.length) {
    return;
  }
  optionalFields.forEach(key => {
    if (!isPresentNumericCandidate(candidate, key)) {
      addIssue(issues, key, partialPrimaryDnaDetailsMessage);
    }
  });
}

type PrimaryDnaDetailCompletion = 'none' | 'partial' | 'complete';

function getPrimaryDnaDetailCompletionFromCount(presentCount: number, fieldCount: number): PrimaryDnaDetailCompletion {
  if (presentCount === 0) {
    return 'none';
  }
  if (presentCount === fieldCount) {
    return 'complete';
  }
  return 'partial';
}

export function getPrimaryDnaDetailCompletion(
  row: SampleSheetDraftRow,
  context: RowValidationContext,
): PrimaryDnaDetailCompletion {
  const optionalFields = getOptionalPrimaryDnaDetailFields(context);
  const presentCount = optionalFields.reduce((count, key) => {
    const parsed = parseSampleMetadataNumberCell(row[key]);
    return typeof parsed === 'number' && Number.isFinite(parsed) ? count + 1 : count;
  }, 0);
  return getPrimaryDnaDetailCompletionFromCount(presentCount, optionalFields.length);
}

function getMissingRequiredQualityFields(
  candidate: Partial<Record<keyof SampleSpecRow, unknown>>,
): ConfirmableSampleMetadataField[] {
  const elutedKeys = ['primElutedDnaQuality280', 'primElutedDnaQuality230'] as const;
  const dilutedKeys = ['primDilutedDnaQuality280', 'primDilutedDnaQuality230'] as const;
  const missingElutedKeys = elutedKeys.filter(key => candidate[key] == null);
  const missingDilutedKeys = dilutedKeys.filter(key => candidate[key] == null);
  const elutedProvidedCount = elutedKeys.length - missingElutedKeys.length;
  const dilutedProvidedCount = dilutedKeys.length - missingDilutedKeys.length;

  if (missingElutedKeys.length === 0 || missingDilutedKeys.length === 0) {
    return [];
  }
  if (elutedProvidedCount === 0 && dilutedProvidedCount === 0) {
    return [...missingElutedKeys];
  }
  if (elutedProvidedCount > 0) {
    return [...missingElutedKeys];
  }
  return [...missingDilutedKeys];
}

export function getRowMetadataLimitations(
  row: SampleSheetDraftRow,
  context: RowValidationContext = {},
): RowMetadataLimitation[] {
  if (isRowEmpty(row)) {
    return [];
  }
  const sampleType = normalizeSampleType(row.sampleType);
  if (sampleType == null || sampleType === SampleType.CONTROL) {
    return [];
  }

  const limitations: RowMetadataLimitation[] = [];
  const addMissingLimitation = (field: ConfirmableSampleMetadataField, category: SampleMetadataLimitationCategory) => {
    const parsed = parseSampleMetadataNumberCell(row[field]);
    if (parsed !== null) {
      return;
    }
    const isExplicitlyUnavailable = isUnavailableSampleMetadataValue(row[field]);
    if (
      field === 'primDilutedDnaConcentration' &&
      row.primDilutedDnaConcentrationUnavailable === false &&
      !isExplicitlyUnavailable
    ) {
      return;
    }
    limitations.push({
      field,
      category,
      reason:
        isExplicitlyUnavailable ||
        (field === 'primDilutedDnaConcentration' && row.primDilutedDnaConcentrationUnavailable === true)
          ? 'not_available'
          : 'missing',
      value: null,
    });
  };

  if (isWaterType(sampleType)) {
    addMissingLimitation('filteredVolume', 'volume_normalisation');
  }

  (
    [
      ['primElutedDnaVolume', 'volume_normalisation'],
      ['primElutedDnaConcentration', 'volume_normalisation'],
      ['primDilutedDnaConcentration', 'volume_normalisation'],
      ['primDilutedDnaVolume', 'sample_volume'],
    ] as const
  ).forEach(([field, category]) => {
    if (!canSkipPrimaryDnaRequirement(field, context)) {
      addMissingLimitation(field, category);
    }
  });

  if (!context.allowMissingPrimaryDnaDetails) {
    const qualityCandidate = {
      primElutedDnaQuality280: parseSampleMetadataNumberCell(row.primElutedDnaQuality280),
      primElutedDnaQuality230: parseSampleMetadataNumberCell(row.primElutedDnaQuality230),
      primDilutedDnaQuality280: parseSampleMetadataNumberCell(row.primDilutedDnaQuality280),
      primDilutedDnaQuality230: parseSampleMetadataNumberCell(row.primDilutedDnaQuality230),
    };
    getMissingRequiredQualityFields(qualityCandidate).forEach(field => {
      addMissingLimitation(field, 'sample_quality');
    });
  }

  const dilutedVolume = parseSampleMetadataNumberCell(row.primDilutedDnaVolume);
  const recommendedMinimum =
    typeof context.dilutedVolumeMin === 'number' && Number.isFinite(context.dilutedVolumeMin)
      ? context.dilutedVolumeMin
      : 100;
  const absoluteMinimum =
    typeof context.dilutedVolumeAbsoluteMin === 'number' && Number.isFinite(context.dilutedVolumeAbsoluteMin)
      ? context.dilutedVolumeAbsoluteMin
      : null;
  if (
    typeof dilutedVolume === 'number' &&
    Number.isFinite(dilutedVolume) &&
    dilutedVolume < recommendedMinimum &&
    absoluteMinimum != null &&
    dilutedVolume >= absoluteMinimum
  ) {
    limitations.push({
      field: 'primDilutedDnaVolume',
      category: 'sample_volume',
      reason: 'below_recommended_minimum',
      value: dilutedVolume,
      recommendedMinimum,
      absoluteMinimum,
    });
  }

  return limitations;
}

export function hasMixedPrimaryDnaDetailCompletion(
  rows: readonly SampleSheetDraftRow[],
  context: RowValidationContext,
): boolean {
  if (!context.allowMissingPrimaryDnaDetails || context.allowMissingElutedDnaBypass) {
    return false;
  }
  const applicableRows = rows.filter(
    row => !isRowEmpty(row) && normalizeSampleType(row.sampleType) !== SampleType.CONTROL,
  );
  const states = applicableRows.map(row => getPrimaryDnaDetailCompletion(row, context));
  return states.includes('none') && states.includes('complete');
}

function addIssue(
  keyedIssues: RowValidationIssues,
  key: keyof SampleSheetDraftRow,
  message: string | undefined | null,
) {
  if (!message) {
    return;
  }
  const existing = keyedIssues[key];
  if (existing && existing.includes(message)) {
    return;
  }
  keyedIssues[key] = [...(existing ?? []), message];
}

function formatZodIssue(issue: z.ZodIssue, key: keyof SampleSheetDraftRow): string {
  const overrides = fieldIssueMessages[key];

  if (issue.code === 'invalid_enum_value') {
    return overrides?.enum ?? 'Choose a value from the available options.';
  }
  if (issue.code === 'invalid_type') {
    if (issue.received === 'null' || issue.received === 'undefined') {
      return overrides?.required ?? 'This field is required.';
    }
    return overrides?.invalid ?? 'Enter a valid value.';
  }
  if (issue.code === 'invalid_date') {
    return overrides?.date ?? overrides?.invalid ?? 'Enter a valid date in YYYY-MM-DD format.';
  }
  if (overrides?.invalid) {
    return overrides.invalid;
  }
  return issue.message || 'Enter a valid value.';
}

export function isRowEmpty(row: SampleSheetDraftRow): boolean {
  return sampleSpecColumnKeys.every(key => {
    const value = row[key];
    if (value == null) return true;
    if (typeof value === 'string') {
      return normalizeSpaces(value).trim() === '';
    }
    return false;
  });
}

function hasValidationIssues(issues: RowValidationIssues): boolean {
  return Object.values(issues).some(messages => Array.isArray(messages) && messages.length > 0);
}

function canBypassIssueKey(
  row: SampleSheetDraftRow,
  key: keyof SampleSheetDraftRow,
  context: RowValidationContext,
): boolean {
  if (adminBypassableFields.has(key)) {
    return context.allowMissingElutedDnaBypass === true && parseSampleMetadataNumberCell(row[key]) === null;
  }
  if (key === 'filteredVolume') {
    const sampleType = normalizeSampleType(row.sampleType);
    return (
      sampleType != null &&
      isWaterType(sampleType) &&
      parseSampleMetadataNumberCell(row.filteredVolume) === null &&
      (context.allowMissingFilteredVolumeBypass === true || context.allowMissingElutedDnaBypass === true)
    );
  }
  if (key === 'primDilutedDnaVolume') {
    if (!context.allowMissingElutedDnaBypass) {
      return false;
    }
    const absoluteMin = context.dilutedVolumeAbsoluteMin;
    if (typeof absoluteMin !== 'number' || !Number.isFinite(absoluteMin)) {
      return false;
    }
    const parsed = parseNumberCell(row.primDilutedDnaVolume);
    if (typeof parsed !== 'number' || !Number.isFinite(parsed)) {
      return false;
    }
    return parsed >= absoluteMin;
  }
  return false;
}

export function isRowReadyForSubmit(row: SampleSheetDraftRow, context: RowValidationContext = {}): boolean {
  if (row.isReadOnly || isRowEmpty(row)) {
    return false;
  }
  const issues = validateRow(row, context);
  if (!hasValidationIssues(issues)) {
    return true;
  }

  const allowBypass = context.allowMissingElutedDnaBypass === true || context.allowMissingFilteredVolumeBypass === true;
  if (!allowBypass) {
    return false;
  }

  const issueKeys = Object.entries(issues)
    .filter(([, messages]) => Array.isArray(messages) && messages.length > 0)
    .map(([key]) => key as keyof SampleSheetDraftRow);

  if (issueKeys.length === 0) {
    return false;
  }

  return issueKeys.every(key => canBypassIssueKey(row, key, context));
}
